/**
 * API consultorio — todo en PostgreSQL (sin localStorage para datos clínicos).
 */
import api from './client';
import { getApiV1Base } from '../utils/apiBase';

const BASE = '/consultorio-clinical';

export const RECORD_TYPES = {
  DENTAL_CHART: 'dental_chart',
  RADIOGRAPH: 'radiograph',
  PRESCRIPTION: 'prescription',
  PAYMENT: 'payment',
  TREATMENT_SESSION: 'treatment_session',
  PRODUCT: 'product',
  CLINIC_INVOICE: 'clinic_invoice',
  DOCTOR_CATALOG: 'doctor_catalog',
  CONSENT_TEMPLATE: 'consent_template',
  PERIODONTOGRAM: 'periodontogram',
  UI_PREF: 'ui_pref',
};

export async function fetchPatients(doctorIds) {
  const ids = Array.isArray(doctorIds) ? doctorIds : [doctorIds];
  const q = ids.filter((x) => x != null && x !== '').map(String).join(',');
  const { data } = await api.get(`${BASE}/patients`, { params: q ? { doctor_ids: q } : {} });
  return Array.isArray(data) ? data : [];
}

export async function savePatientApi(externalId, patient) {
  const id = externalId || patient.id || `pac-${Date.now()}`;
  const { data } = await api.put(`${BASE}/patients/${encodeURIComponent(id)}`, patient);
  return data;
}

export async function fetchAppointments(doctorIds) {
  const ids = Array.isArray(doctorIds) ? doctorIds : [doctorIds];
  const q = ids.filter((x) => x != null && x !== '').map(String).join(',');
  const { data } = await api.get(`${BASE}/appointments`, { params: q ? { doctor_ids: q } : {} });
  return Array.isArray(data) ? data : [];
}

export async function saveAppointmentApi(externalId, appointment) {
  const id = externalId || appointment.id || `cita-${Date.now()}`;
  const { data } = await api.put(`${BASE}/appointments/${encodeURIComponent(id)}`, appointment);
  return data;
}

export async function fetchRecords(recordType, { patientId, doctorIds } = {}) {
  const params = {};
  if (patientId) params.patient_id = patientId;
  if (doctorIds != null) {
    const ids = Array.isArray(doctorIds) ? doctorIds : [doctorIds];
    const q = ids.filter((x) => x != null && x !== '').map(String).join(',');
    if (q) params.doctor_ids = q;
  }
  const { data } = await api.get(`${BASE}/records/${recordType}`, { params });
  return Array.isArray(data) ? data : [];
}

export async function saveRecordApi(recordType, externalId, body) {
  const id = externalId || body.id || `${recordType}-${Date.now()}`;
  const { data } = await api.put(
    `${BASE}/records/${recordType}/${encodeURIComponent(id)}`,
    { ...body, id },
  );
  return data;
}

export async function deleteRecordApi(recordType, externalId) {
  await api.delete(`${BASE}/records/${recordType}/${encodeURIComponent(externalId)}`);
}

export async function uploadClinicalFile({ externalId, dataUrl, contentBase64, mime, filename, meta }) {
  const { data } = await api.post(`${BASE}/files`, {
    external_id: externalId,
    data_url: dataUrl || undefined,
    content_base64: contentBase64 || undefined,
    mime,
    filename,
    meta: meta || {},
  });
  return data;
}

export async function getClinicalFileMeta(externalId) {
  const { data } = await api.get(`${BASE}/files/${encodeURIComponent(externalId)}`);
  return data;
}

export function clinicalFileAbsoluteUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const base = getApiV1Base().replace(/\/api\/v1\/?$/, '');
  return `${base}${url.startsWith('/') ? url : `/${url}`}`;
}

export async function deleteClinicalFileApi(externalId) {
  await api.delete(`${BASE}/files/${encodeURIComponent(externalId)}`);
}

export async function fetchStorageUsage() {
  const { data } = await api.get(`${BASE}/storage-usage`);
  return data;
}

export async function migrateFullLocal(payload) {
  const { data } = await api.post(`${BASE}/migrate-full`, payload);
  return data;
}

export async function fetchClientState() {
  const { data } = await api.get(`${BASE}/client-state`);
  return data && typeof data === 'object' ? data : { migrated_full: false };
}

export async function saveClientState(state) {
  const { data } = await api.put(`${BASE}/client-state`, state || {});
  return data;
}
